// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { TimezoneAutocomplete } from '@/components/timezone-autocomplete';
import { DateTime, IANAZone, Zone } from 'luxon';
import { ForwardedRef, forwardRef, useMemo } from 'react';

type TimezoneValue<DisableClearable> = DisableClearable extends true
  ? NonNullable<Zone>
  : Zone | null;

interface Props<DisableClearable extends boolean | undefined> {
  /**
   * The name of the form input element.
   */
  name?: string;

  /**
   * The label attached to the input element.
   */
  label?: string;

  /**
   * If true, the component will be displayed in an error state.
   */
  error?: boolean;

  /**
   * If provided, the helper text will be displayed below the input element.
   */
  helperText?: string;

  /**
   * The currently selected timezone.
   */
  value: TimezoneValue<DisableClearable>;

  /**
   * If provided, the reference date and time will be used to determine the
   * option names. Otherwise, the current date and time will be used.
   */
  reference?: DateTime;

  /**
   * If true, the input can't be cleared.
   */
  disableClearable?: DisableClearable;

  /**
   * A callback fired when the selected timezone changes.
   */
  onChange: (value: TimezoneValue<DisableClearable>) => void;
}

/**
 * Renders a timezone autocomplete component with options from the Intl API.
 * This function also forwards a ref to the autocomplete component.
 */
function TimezoneSearchRenderer<DisableClearable extends boolean | undefined>(
  props: Props<DisableClearable>,
  ref: ForwardedRef<HTMLInputElement>
) {
  const timezones = useMemo(() => {
    const supportedValues = Intl.supportedValuesOf('timeZone');
    const initialValues = props.value ? [props.value] : [];
    const options = supportedValues.reduce((values, value) => {
      try {
        const timezone = IANAZone.create(value);

        if (values.some((other) => other.name === timezone.name)) {
          return values;
        }

        return [...values, IANAZone.create(value)];
      } catch (error) {
        Sentry.captureException(error);
      }

      return values;
    }, initialValues);

    return options.sort((timezone, other) => {
      return timezone.name.localeCompare(other.name);
    });
  }, [props.value]);

  const handleChange = (_: unknown, value: TimezoneValue<DisableClearable>) => {
    props.onChange(value);
  };

  return (
    <TimezoneAutocomplete
      ref={ref}
      size="small"
      name={props.name}
      value={props.value}
      error={props.error}
      label={props.label}
      options={timezones}
      onChange={handleChange}
      onBlur={(e) => {
        e.target.scrollTo(0, 0);
      }}
      helperText={props.helperText}
      reference={props.reference}
      disableClearable={props.disableClearable}
    />
  );
}

/**
 * A controlled component for selecting a timezone from a list of options from
 * the remote API.
 */
export const TimezoneSearch = forwardRef(TimezoneSearchRenderer);
